import { ReactElement, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Controller } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import useSwiperRef from '~/ui/components/common/Swiper/useSwiperRef';

interface NewsSectionProps {
  isVisible?: boolean;
}

const NewsSection = ({ isVisible = true }: NewsSectionProps): ReactElement | null => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$newsSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$newsSection' });

  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();

  const paginationRef = useRef(null);

  const [swiperInstance1, setSwiperInstance1] = useState(null);
  const [swiperInstance2, setSwiperInstance2] = useState(null);

  useEffect(() => {
    if (swiperInstance1 && paginationRef.current) {
      swiperInstance1.params.pagination.el = paginationRef.current;

      swiperInstance1.pagination.destroy();
      swiperInstance1.pagination.init();
      swiperInstance1.pagination.update();
    }
  }, [swiperInstance1]);

  const NewsSlider = {
    slides: [
      {
        id: 1,
        title: t('firstSlideTitle'),
        description: t('firstSlideDescription'),
        image:
          '/images/homePage/newsSection/sofitel-sateen-duvet-cover-pillow-sham-set-beige-white-trim.jpg',
        link: tLink('sateenCollection'),
        sortOrder: 1,
      },
      {
        id: 2,
        title: t('secondSlideTitle'),
        description: t('secondSlideDescription'),
        image: '/images/homePage/newsSection/sofitel-essence-de-sofitel-collection.jpg',
        link: tLink('essenceDeSofitel'),
        sortOrder: 2,
      },
    ],
  };

  // Reorder slides from NewSlider object by the sortOrder property
  const sortedSlides = [...NewsSlider.slides].sort((a, b) => a.sortOrder - b.sortOrder);

  // Check if the section should be visible
  if (!isVisible) {
    return null;
  }

  return (
    <section id="hp-news" className="hp-section news-section news">
      <h2>{t('title')}</h2>
      <div className="carousel-wrapper">
        <div id="hp-swiper-news-copy" className="swiper">
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slidesPerView={1}
            navigation={{
              prevEl,
              nextEl,
            }}
            pagination={{
              el: paginationRef.current,
              type: 'fraction',
              renderFraction: (currentClass, totalClass) =>
                `<span class="${currentClass}"></span>&nbsp;/&nbsp;<span class="${totalClass}"></span>`,
            }}
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-wrapper"
            modules={[Navigation, Pagination, A11y, Controller]}
            onSwiper={setSwiperInstance1}
            controller={{ control: swiperInstance2 }}
          >
            {sortedSlides.map(slide => (
              <SwiperSlide key={slide.id} className="swiper-slide">
                <Link
                  href={slide.link}
                  className="clearfix"
                  data-event-name="bloc_interact"
                  data-bloc-name={slide.title}
                >
                  <div className="stack">
                    <h3>{slide.title}</h3>
                    <p>{slide.description}</p>
                    <span className="btn base-btn btn-plain">
                      <span>{tButton('shopNow')}</span>
                    </span>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="swiper-actions swiper-buttons">
            <button
              id="news-slide-prev"
              className="swiper-button swiper-button-prev"
              type="button"
              aria-label="Previous button is disabled"
              ref={prevElRef}
            >
              <span className="icon icon-arrow" />
            </button>
            <div className="carousel-pages" ref={paginationRef} />
            <button
              id="news-slide-next"
              className="swiper-button swiper-button-next"
              type="button"
              aria-label="Next, go to the 3/4 slide"
              ref={nextElRef}
            >
              <span className="icon icon-arrow" />
            </button>
          </div>
        </div>
        <div id="hp-swiper-news-images" className="swiper">
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slidesPerView={1}
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-wrapper"
            modules={[A11y, Controller]}
            onSwiper={setSwiperInstance2}
            controller={{ control: swiperInstance1 }}
          >
            {sortedSlides.map(slide => (
              <SwiperSlide key={slide.id} className="swiper-slide">
                <Link
                  href={slide.link}
                  className="clearfix"
                  data-event-name="bloc_interact"
                  data-bloc-name={slide.title}
                >
                  <picture>
                    <source
                      data-srcset={slide.image}
                      media="(max-width: 768px)"
                      srcSet={slide.image}
                    />
                    <img
                      loading="lazy"
                      data-src={slide.image}
                      alt={slide.title}
                      src={slide.image}
                      width="1000"
                      height="1022"
                    />
                  </picture>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
